/**
 * Utility functions for Service Worker registration and communication.
 */

/**
 * Registers the service worker.
 */
export const registerSW = () => {
  if ('serviceWorker' in navigator) {
    // For Vite, use import.meta.env or just use the base URL
    const publicUrl = import.meta.env?.VITE_PUBLIC_URL || ''
    const baseUrl = publicUrl || window.location.origin
    
    // Check if we're on the same origin
    try {
      const url = new URL(baseUrl, window.location.href)
      if (url.origin !== window.location.origin) {
        return
      }
    } catch (e) {
      // If URL parsing fails, assume same origin
    }

    window.addEventListener('load', () => {
      // Use relative path for service worker
      const swUrl = '/sw.js'
      
      navigator.serviceWorker.register(swUrl)
        .then(registration => {
          console.log('Service Worker registered with scope:', registration.scope)
          // Check for updates immediately
          registration.update()
          
          // Handle service worker updates
          registration.onupdatefound = () => {
            const installingWorker = registration.installing
            if (installingWorker == null) {
              return
            }
            
            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  console.log('🔄 Service Worker: New content available, refresh to update')
                  
                  // Notify user about update
                  if (window.showUpdateNotification) {
                    window.showUpdateNotification()
                  }
                } else {
                  console.log('✅ Service Worker: Content cached for offline use')
                  
                  // Notify user about offline capability
                  if (window.showOfflineNotification) {
                    window.showOfflineNotification()
                  }
                }
              }
            }
          }
        })
        .catch(error => {
          console.error('Service Worker registration failed:', error)
        })
    })
  }
}

/**
 * Sets up listeners for messages from the service worker.
 */
export const setupServiceWorkerListeners = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      console.log('Received message from service worker:', event.data)
      switch (event.data.type) {
        case 'UPDATE_AVAILABLE':
          // Prompt user to update
          if (window.showUpdateNotification) {
            window.showUpdateNotification()
          }
          break
        case 'OFFLINE_READY':
          // Notify user that app is ready for offline use
          if (window.showOfflineNotification) {
            window.showOfflineNotification()
          }
          break
        case 'SYNC_COMPLETE':
          // Notify about background sync completion
          if (window.onSyncComplete) {
            window.onSyncComplete(event.data)
          }
          break
        case 'ONLINE':
          if (window.onOnline) {
            window.onOnline()
          }
          break
        case 'OFFLINE':
          if (window.onOffline) {
            window.onOffline()
          }
          break
        default:
          break
      }
    })

    // Listen for controller change (e.g., new service worker activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('Service Worker: Controller changed. Reloading page for update.')
      // window.location.reload() // Reload to ensure new SW takes control
    })
  }
}

/**
 * Sets up listeners for browser online/offline events.
 */
export const setupConnectivityListeners = () => {
  window.addEventListener('online', () => {
    console.log('App is online!')
    // You might want to trigger a sync here
    if (window.onOnline) {
      window.onOnline()
    }
  })

  window.addEventListener('offline', () => {
    console.log('App is offline!')
    if (window.onOffline) {
      window.onOffline()
    }
  })
}

/**
 * Sends a message to the service worker to cache a specific note.
 * @param {object} note - The note object to cache.
 */
export const cacheNote = (note) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_NOTE',
      note: note
    })
  }
}

/**
 * Sends a message to the service worker to preload critical resources.
 * @param {Array<string>} urls - Array of URLs to preload.
 */
export const preloadCriticalResources = (urls = ['/api/folders']) => { // Removed /api/notes - temporarily disabled
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'PRELOAD_RESOURCES',
      urls: urls
    })
  }
}

/**
 * Requests cache status from the service worker.
 * (This is a simplified example, actual implementation might involve a Promise-based response)
 */
export const getCacheStatus = () => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    // For a real implementation, you'd use MessageChannel for a response
    console.warn('getCacheStatus: Direct response from SW not implemented. This is a placeholder.')
    return { hitRate: 0, totalRequests: 0, hits: 0, error: 'Not implemented' }
  }
  return { hitRate: 0, totalRequests: 0, hits: 0, error: 'Service Worker not active' }
} 